import store from "store";
import pusherService from "./pusherService";
import {
  createSmoothedGenerationProgress,
  mapRecordGenerationProgressMessage,
  mapRecordGenerationProgressPercent,
} from "./recordGenerationProgress";
import { performRefresh } from "auth/refreshSession";

const DEFAULT_WAIT_MS = 30 * 60 * 1000;
const DEFAULT_POST_API_GRACE_MS = 8000;
const PROGRESS_IDLE_BUMP_MS = 2000;
const LOG = "[FinalizeFlow]";

function ensurePusherConnected() {
  const user = store.getState()?.auth?.user;
  const accountId = Number(user?.accountId);
  const userId = Number(user?.userId ?? user?.id);
  if (accountId && userId) {
    pusherService.connect(accountId, userId);
    console.log(LOG, "Pusher connect for record generation", {
      channel: `${accountId}-${userId}`,
    });
  } else {
    console.warn(LOG, "Pusher connect skipped — missing auth user", {
      accountId,
      userId,
    });
  }
}

const isAxiosTimeout = (error) =>
  error?.code === "ECONNABORTED" ||
  String(error?.message || "").toLowerCase().includes("timeout");

async function refreshScreenAfterGeneration() {
  try {
    await performRefresh();
  } catch (refreshError) {
    console.error(
      LOG,
      "Session refresh after declaration generation failed:",
      refreshError,
    );
  }
  console.log(LOG, "Reloading page after declaration generation");
  window.location.reload();
}

export function attachRecordGenerationWaiting({
  orderId,
  onProgress,
  onCompleted,
  onFailed,
  timeoutMs = DEFAULT_WAIT_MS,
}) {
  ensurePusherConnected();

  let settled = false;
  let timeoutId = null;
  let unsubProgress = null;
  let unsubFileReady = null;

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    unsubProgress?.();
    unsubProgress = null;
    unsubFileReady?.();
    unsubFileReady = null;
  };

  const settle = (fn, value) => {
    if (settled) return;
    settled = true;
    cleanup();
    fn(value);
  };

  const matchesOrder = (payload) =>
    Number(payload?.orderId) === Number(orderId);

  const handleRecordProgress = (payload) => {
    console.log(LOG, "Pusher record-generation-progress (raw)", payload);

    if (!matchesOrder(payload)) {
      console.log(LOG, "Pusher record-generation-progress ignored (orderId)", {
        expected: orderId,
        received: payload?.orderId,
      });
      return;
    }

    console.log(LOG, "Pusher record-generation-progress (matched)", payload);

    onProgress?.({
      ...payload,
      progress: {
        ...(payload?.progress || {}),
        percentage: mapRecordGenerationProgressPercent(payload),
      },
    });

    const step = String(payload?.step || "").toUpperCase();

    if (step === "COMPLETED") {
      settle(onCompleted, payload);
    } else if (step === "FAILED") {
      settle(onFailed, {
        message:
          payload?.error ||
          payload?.message ||
          "Failed to generate declaration of records.",
        payload,
      });
    }
  };

  const handleFileReady = (payload) => {
    if (!matchesOrder(payload)) return;

    const status = String(payload?.status || "").toUpperCase();

    if (status === "READY") {
      console.log(LOG, "Pusher file-ready (declaration complete)", payload);
      onProgress?.({
        orderId,
        step: "COMPLETED",
        message: "Declaration of records is ready.",
        progress: { percentage: 100, current: 100, total: 100 },
      });
      settle(onCompleted, payload);
    } else if (status === "FAILED") {
      settle(onFailed, {
        message: "Failed to generate declaration of records.",
        payload,
      });
    }
  };

  unsubProgress = pusherService.subscribe(
    "record-generation-progress",
    handleRecordProgress,
  );
  unsubFileReady = pusherService.subscribe("file-ready", handleFileReady);

  timeoutId = setTimeout(() => {
    settle(onFailed, {
      message:
        "Declaration generation timed out. Please refresh and try again.",
      payload: null,
    });
  }, timeoutMs);

  return cleanup;
}

export async function waitForRecordGenerationCompletion({
  orderId,
  requestGeneratePdf,
  onProgress,
  timeoutMs = DEFAULT_WAIT_MS,
  postApiGraceMs = DEFAULT_POST_API_GRACE_MS,
}) {
  if (typeof requestGeneratePdf === "function") {
    return generatePdfAndWaitForDeclaration({
      orderId,
      requestGeneratePdf,
      onProgress,
      timeoutMs,
      postApiGraceMs,
      refreshOnComplete: true,
    });
  }

  const progressSession = createSmoothedGenerationProgress(onProgress);
  progressSession.start();

  let settled = false;

  const idleBumpId = setInterval(() => {
    if (!settled) progressSession.bumpTarget();
  }, PROGRESS_IDLE_BUMP_MS);

  return new Promise((resolve, reject) => {
    attachRecordGenerationWaiting({
      orderId,
      onProgress: (payload) => {
        if (settled) return;
        progressSession.push(payload);
      },
      timeoutMs,
      onCompleted: async (payload) => {
        if (settled) return;
        settled = true;
        clearInterval(idleBumpId);

        try {
          await progressSession.complete(
            mapRecordGenerationProgressMessage(payload) ||
              "Declaration of records is ready.",
          );
        } finally {
          progressSession.stop();
        }

        await refreshScreenAfterGeneration();
        resolve(payload);
      },
      onFailed: ({ message }) => {
        if (settled) return;
        settled = true;
        clearInterval(idleBumpId);
        progressSession.stop();
        reject(new Error(message));
      },
    });
  });
}

export async function generatePdfAndWaitForDeclaration({
  orderId,
  requestGeneratePdf,
  onProgress,
  timeoutMs = DEFAULT_WAIT_MS,
  refreshOnComplete = true,
  postApiGraceMs = DEFAULT_POST_API_GRACE_MS,
}) {
  const progressSession = createSmoothedGenerationProgress(onProgress);
  progressSession.start();

  let cleanup = null;
  let settled = false;

  const idleBumpId = setInterval(() => {
    if (!settled) progressSession.bumpTarget();
  }, PROGRESS_IDLE_BUMP_MS);

  const waitPromise = new Promise((resolve, reject) => {
    cleanup = attachRecordGenerationWaiting({
      orderId,
      onProgress: (payload) => {
        if (settled) return;
        progressSession.push(payload);
      },
      timeoutMs,
      onCompleted: (payload) => resolve({ kind: "completed", payload }),
      onFailed: ({ message }) =>
        reject(
          Object.assign(new Error(message), { __pusherFailure: true }),
        ),
    });
  });

  waitPromise.catch(() => {});

  const apiStartedAt = Date.now();
  let apiSucceeded = false;

  try {
    console.log(LOG, "generatePDF API request start", { orderId });

    try {
      await requestGeneratePdf();
      apiSucceeded = true;
      console.log(
        LOG,
        "generatePDF API request finished (awaiting Pusher or grace)",
        {
          orderId,
          apiElapsedMs: Date.now() - apiStartedAt,
          postApiGraceMs,
        },
      );
    } catch (error) {
      if (!isAxiosTimeout(error)) {
        console.log(LOG, "generatePDF API request failed hard", {
          orderId,
          message: error?.message,
        });
        cleanup?.();
        throw error;
      }

      console.log(
        LOG,
        "generatePDF API request timeout — waiting on Pusher",
        {
          orderId,
          message: error?.message,
        },
      );
      progressSession.push({
        orderId,
        step: "PROGRESS",
        message:
          "Request timed out; still waiting for declaration generation on the server…",
        progress: { percentage: 0, current: 0, total: 100 },
      });
    }

    const outcome = apiSucceeded
      ? await Promise.race([
          waitPromise,
          new Promise((resolve) =>
            setTimeout(
              () => resolve({ kind: "grace-timeout" }),
              postApiGraceMs,
            ),
          ),
        ])
      : await waitPromise;

    if (outcome.kind === "completed") {
      settled = true;
      await progressSession.complete(
        mapRecordGenerationProgressMessage(outcome.payload) ||
          "Declaration of records is ready.",
      );
      if (refreshOnComplete) {
        await refreshScreenAfterGeneration();
      }
      return outcome.payload;
    }

    if (outcome.kind === "grace-timeout") {
      settled = true;
      console.log(
        LOG,
        "Pusher completion not received within grace window — trusting HTTP 2xx",
        { orderId, postApiGraceMs },
      );
      cleanup?.();
      await progressSession.complete("Declaration of records is ready.");
      if (refreshOnComplete) {
        await refreshScreenAfterGeneration();
      }
      return {
        orderId,
        step: "COMPLETED",
        message: "Declaration of records is ready.",
      };
    }

    throw new Error("Unexpected generation wait outcome");
  } finally {
    settled = true;
    clearInterval(idleBumpId);
    progressSession.stop();
  }
}

export { isAxiosTimeout };